import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Segment,
  Button,
  Icon,
  Table,
  Dropdown,
  Dimmer,
  Loader,
  Message,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../../helpers';
import { normalizeNumeric } from '../../../helpers/render';
import BillingSummaryCards from './BillingSummaryCards';
import BillingDailyDetail from './BillingDailyDetail';

const PROVIDER_NAMES = {
  15: '百度千帆',
  17: '阿里',
  40: '字节豆包',
  47: '百度千帆V2',
};

const BillingOverview = () => {
  const { t } = useTranslation();
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      '0'
    )}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncNotice, setSyncNotice] = useState('');
  const [expandedConfig, setExpandedConfig] = useState(null);
  const [providerFilter, setProviderFilter] = useState([]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month });
      const res = await API.get(`/api/admin/channel-billing/records?${params}`);
      if (res.data.success) {
        setData(res.data.data);
      } else {
        showError(res.data.message);
      }
    } catch (error) {
      showError(t('channel_billing.load_error', '加载失败'));
    } finally {
      setLoading(false);
    }
  }, [month, t]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncNotice('');
    try {
      const res = await API.post('/api/admin/channel-billing/sync');
      if (res.data.success) {
        const results = res.data.data || [];
        const failed = results.filter((result) => !result.success);
        const skipped = results.filter((result) => result.skipped);
        if (failed.length > 0) {
          showError(
            failed
              .map(
                (result) =>
                  `${result.config_name}: ${
                    result.error || t('channel_billing.sync_error', '同步失败')
                  }`
              )
              .join('；')
          );
        } else if (
          results.some((result) => result.success && !result.skipped)
        ) {
          showSuccess(t('channel_billing.sync_success', '同步完成'));
        }
        if (skipped.length > 0) {
          setSyncNotice(
            `${skipped.map((result) => result.config_name).join('、')}：${t(
              'channel_billing.manual_sync_skipped',
              '账单需手工导入，本次同步已跳过'
            )}`
          );
        } else if (results.length === 0) {
          setSyncNotice(
            t('channel_billing.no_sync_configs', '暂无已启用的账单配置')
          );
        }
        await fetchRecords();
      } else {
        showError(res.data.message);
      }
    } catch (error) {
      showError(t('channel_billing.sync_error', '同步失败'));
    } finally {
      setSyncing(false);
    }
  };

  const monthOptions = (() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        '0'
      )}`;
      opts.push({
        key: val,
        text: `${d.getFullYear()}年${d.getMonth() + 1}月`,
        value: val,
      });
    }
    return opts;
  })();

  const summary = data?.summary || [];
  const stale = data?.stale || false;
  const filteredSummary =
    providerFilter.length > 0
      ? summary.filter((s) => providerFilter.includes(s.provider_type))
      : summary;

  const providerOptions = [...new Set(summary.map((s) => s.provider_type))].map(
    (pt) => ({
      key: pt,
      text: PROVIDER_NAMES[pt] || `Provider ${pt}`,
      value: pt,
    })
  );

  return (
    <>
      {syncNotice && <Message info content={syncNotice} />}
      {stale && (
        <Message warning>
          <Message.Content>
            {t(
              'channel_billing.stale_warning',
              '数据已超过24小时未更新，建议同步'
            )}
          </Message.Content>
        </Message>
      )}

      <Segment>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <Dropdown
              selection
              options={monthOptions}
              value={month}
              onChange={(e, { value }) => setMonth(value)}
              style={{ minWidth: 150, fontWeight: 'bold' }}
            />
            <Dropdown
              placeholder={t('channel_billing.filter_provider', '筛选提供商')}
              multiple
              selection
              clearable
              options={providerOptions}
              value={providerFilter}
              onChange={(e, { value }) => setProviderFilter(value)}
              style={{ marginLeft: 16, minWidth: 200 }}
            />
          </div>
          <div>
            <Button
              primary
              loading={syncing}
              disabled={syncing}
              onClick={handleSync}
            >
              <Icon name='sync' />
              {t('channel_billing.sync_btn', '同步账单')}
            </Button>
            <Button onClick={fetchRecords} disabled={loading}>
              <Icon name='refresh' />
              {t('channel_billing.refresh_btn', '刷新')}
            </Button>
          </div>
        </div>
      </Segment>

      {loading ? (
        <Segment style={{ minHeight: 200 }}>
          <Dimmer active inverted>
            <Loader>{t('channel_billing.loading', '加载中...')}</Loader>
          </Dimmer>
        </Segment>
      ) : (
        <>
          <Segment>
            <BillingSummaryCards summary={filteredSummary} />
          </Segment>

          <Segment>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    {t('channel_billing.table.config_name', '配置名称')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('channel_billing.table.provider', '提供商')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('channel_billing.table.input_tokens', '输入 Token')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('channel_billing.table.output_tokens', '输出 Token')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('channel_billing.table.total_tokens', '总 Token')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('channel_billing.table.amount', '金额 (CNY)')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('channel_billing.table.calls', '调用次数')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('channel_billing.table.action', '操作')}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredSummary.map((item) => (
                  <React.Fragment key={item.config_id}>
                    <Table.Row>
                      <Table.Cell>{item.config_name}</Table.Cell>
                      <Table.Cell>
                        {PROVIDER_NAMES[item.provider_type] ||
                          item.provider_type}
                      </Table.Cell>
                      <Table.Cell>
                        {(item.total_input_tokens || 0).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell>
                        {(item.total_output_tokens || 0).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell>
                        {(item.total_tokens || 0).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell>
                        {(normalizeNumeric(item.total_amount_cny) ?? 0) > 0
                          ? `¥${(
                              normalizeNumeric(item.total_amount_cny) ?? 0
                            ).toFixed(2)}`
                          : '-'}
                      </Table.Cell>
                      <Table.Cell>
                        {(item.total_call_count || 0).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          size='small'
                          onClick={() =>
                            setExpandedConfig(
                              expandedConfig === item.config_id
                                ? null
                                : item.config_id
                            )
                          }
                        >
                          {expandedConfig === item.config_id
                            ? t('channel_billing.table.collapse', '收起')
                            : t('channel_billing.table.detail', '详情')}
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                    {expandedConfig === item.config_id && (
                      <Table.Row>
                        <Table.Cell colSpan={8}>
                          <BillingDailyDetail
                            records={data?.daily_details}
                            configId={item.config_id}
                          />
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </React.Fragment>
                ))}
                {filteredSummary.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan={8} textAlign='center'>
                      {t('channel_billing.table.no_data', '暂无数据')}
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Segment>
        </>
      )}
    </>
  );
};

export default BillingOverview;
